import { useCallback, useEffect, useRef, useState } from 'react'
import { router } from '@inertiajs/react'
import { useDebouncedCallback } from 'use-debounce'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import TipTapEditor from '@/components/editor/TipTapEditor'
import { Trash2, Check, Loader2, AlertCircle } from 'lucide-react'

interface TaskLabel {
  id: string
  name: string
  color: string
}

type Priority = 'no_priority' | 'low' | 'medium' | 'high' | 'urgent'

interface TaskItem {
  id: string
  title: string
  description: string | null
  position: number
  priority: Priority
  assignee: { id: string; name: string } | null
  labels: TaskLabel[]
  updated_at: string
}

interface UserOption {
  id: string
  name: string
  email: string
}

interface Props {
  task: TaskItem | null
  users: UserOption[]
  boardSlug: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onTaskUpdated?: (task: TaskItem) => void
}

type SaveStatus = 'saved' | 'saving' | 'unsaved' | 'error' | 'idle'

function getCsrfToken(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''
}

export default function TaskDrawer({ task, users, boardSlug, open, onOpenChange, onTaskUpdated }: Props) {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle')
  const [statusVisible, setStatusVisible] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [assigneeId, setAssigneeId] = useState<string>(task?.assignee?.id || '')
  const [priority, setPriority] = useState<Priority>(task?.priority || 'no_priority')

  const titleRef = useRef(task?.title || '')
  const descRef = useRef(task?.description || '')
  const assigneeIdRef = useRef(task?.assignee?.id || '')
  const priorityRef = useRef<Priority>(task?.priority || 'no_priority')
  const dirtyRef = useRef(false)
  const statusTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const titleInputRef = useRef<HTMLInputElement>(null)

  // Sync refs and state when task changes
  useEffect(() => {
    if (task) {
      titleRef.current = task.title
      descRef.current = task.description || ''
      assigneeIdRef.current = task.assignee?.id || ''
      setAssigneeId(task.assignee?.id || '')
      priorityRef.current = task.priority || 'no_priority'
      setPriority(task.priority || 'no_priority')
      dirtyRef.current = false
      setSaveStatus('idle')
      setDeleteConfirm(false)
    }
  }, [task?.id])

  const flashStatus = useCallback((status: SaveStatus) => {
    setSaveStatus(status)
    setStatusVisible(true)
    if (statusTimeoutRef.current) clearTimeout(statusTimeoutRef.current)
    if (status === 'saved') {
      statusTimeoutRef.current = setTimeout(() => setStatusVisible(false), 2000)
    }
  }, [])

  const performAutosave = useCallback(async () => {
    if (!task || !dirtyRef.current) return

    flashStatus('saving')

    try {
      const response = await fetch(`/tasks/${task.id}/autosave`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
        },
        body: JSON.stringify({
          task: {
            title: titleRef.current,
            description: descRef.current,
            assignee_id: assigneeIdRef.current || null,
            priority: priorityRef.current,
          },
        }),
      })

      if (response.ok) {
        const data = await response.json()
        dirtyRef.current = false
        flashStatus('saved')
        if (data.task) {
          onTaskUpdated?.({ ...task, ...data.task })
        }
      } else {
        flashStatus('error')
      }
    } catch {
      flashStatus('error')
    }
  }, [task?.id, flashStatus, onTaskUpdated])

  const debouncedAutosave = useDebouncedCallback(performAutosave, 2000)

  const markDirty = useCallback(() => {
    dirtyRef.current = true
    flashStatus('unsaved')
    debouncedAutosave()
  }, [debouncedAutosave, flashStatus])

  // Cmd+S handler
  useEffect(() => {
    if (!open) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault()
        debouncedAutosave.cancel()
        performAutosave()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, performAutosave, debouncedAutosave])

  // Save on close
  const handleOpenChange = useCallback((newOpen: boolean) => {
    if (!newOpen && dirtyRef.current) {
      debouncedAutosave.cancel()
      performAutosave()
    }
    onOpenChange(newOpen)
  }, [onOpenChange, debouncedAutosave, performAutosave])

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    titleRef.current = e.target.value
    markDirty()
  }

  const handleDescriptionChange = (markdown: string) => {
    descRef.current = markdown
    markDirty()
  }

  const handleAssigneeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newId = e.target.value
    setAssigneeId(newId)
    assigneeIdRef.current = newId
    markDirty()
  }

  const handlePriorityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value as Priority
    setPriority(val)
    priorityRef.current = val
    markDirty()
  }

  const handleDelete = () => {
    if (!task) return
    if (!deleteConfirm) {
      setDeleteConfirm(true)
      return
    }
    router.delete(`/tasks/${task.id}`, {
      preserveScroll: true,
      onSuccess: () => onOpenChange(false),
    })
  }

  if (!task) return null

  const statusConfig: Record<SaveStatus, { icon: React.ReactNode; text: string } | null> = {
    saved: { icon: <Check className="h-3 w-3" />, text: 'Saved' },
    saving: { icon: <Loader2 className="h-3 w-3 animate-spin" />, text: 'Saving...' },
    unsaved: null,
    error: { icon: <AlertCircle className="h-3 w-3" />, text: 'Save failed' },
    idle: null,
  }

  const status = statusConfig[saveStatus]

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        className="w-[600px] sm:max-w-[600px] bg-white border-gray-200 flex flex-col p-0"
        showCloseButton
        overlay={false}
      >
        <SheetHeader className="p-6 pb-0">
          <SheetTitle className="sr-only">Edit Task</SheetTitle>

          {/* Editable title */}
          <input
            ref={titleInputRef}
            defaultValue={task.title}
            onChange={handleTitleChange}
            key={task.id}
            className="w-full bg-transparent text-xl font-semibold text-gray-900 outline-none border-none placeholder:text-gray-400"
            placeholder="Task title..."
          />
        </SheetHeader>

        {/* Metadata row */}
        <div className="px-6 py-3 flex items-center gap-4 border-b border-gray-100">
          {/* Assignee */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">Assignee</span>
            <select
              value={assigneeId}
              onChange={handleAssigneeChange}
              className="bg-transparent text-xs text-gray-700 border-none outline-none cursor-pointer hover:text-gray-900 transition-colors"
            >
              <option value="">Unassigned</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>
          </div>

          {/* Priority */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">Priority</span>
            <select
              value={priority}
              onChange={handlePriorityChange}
              className="bg-transparent text-xs text-gray-700 border-none outline-none cursor-pointer hover:text-gray-900 transition-colors"
            >
              <option value="no_priority">None</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>

          {/* Labels */}
          {task.labels.length > 0 && (
            <div className="flex items-center gap-1">
              {task.labels.map(label => (
                <Badge
                  key={label.id}
                  variant="outline"
                  className="text-[10px] px-1.5 py-0"
                  style={{ borderColor: label.color, color: label.color }}
                >
                  {label.name}
                </Badge>
              ))}
            </div>
          )}

          {/* Save status */}
          {status && statusVisible && (
            <span
              className={`ml-auto flex items-center gap-1 text-xs transition-opacity duration-300 ${
                saveStatus === 'error' ? 'text-red-500' : 'text-gray-400'
              }`}
            >
              {status.icon}
              {status.text}
            </span>
          )}
        </div>

        {/* TipTap editor */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <TipTapEditor
            key={task.id}
            content={task.description || ''}
            onChange={handleDescriptionChange}
            onSave={() => {
              debouncedAutosave.cancel()
              performAutosave()
            }}
            borderless
            showBubbleMenu
            showSlashCommands
            placeholder="Add a description..."
          />
        </div>

        {/* Footer */}
        <SheetFooter className="border-t border-gray-200 p-4 flex-row justify-between items-center">
          <Button
            variant={deleteConfirm ? 'destructive' : 'ghost'}
            size="sm"
            onClick={handleDelete}
            className={deleteConfirm ? '' : 'text-gray-400 hover:text-red-500'}
          >
            <Trash2 className="mr-1 h-3 w-3" />
            {deleteConfirm ? 'Confirm delete' : 'Delete'}
          </Button>
          <span className="text-[11px] text-gray-400">
            Updated {new Date(task.updated_at).toLocaleString()}
          </span>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
